import * as O from 'fp-ts/lib/Option';
import { Filter } from './Filter';
import { Filter_Config, Filter_ControlsState, Filter_Snapshot, FilterAggregation } from './types/filters.type';
import { FilterType } from './types/filterType.type';
import {
    CancelKeyWordsFilterPayload,
    KeyWordFilter_Config,
    KeyWordFilter_ControlsState,
    KeyWordFilter_RequestParams,
    KeyWordFilter_Snapshot,
    KeyWordFilter_State,
    KeyWordFilterAggregation,
    KeyWordFilterKey,
    KeyWordFilterKey_State,
    UpdateKeyWordsFilterPayload_ControlsState,
} from './types/keywordFilter.type';
import { NumberFilter_Config } from './types/numberFilter.type';

const keywordsSortByCount = (
    a: KeyWordFilterKey_State | KeyWordFilterKey,
    b: KeyWordFilterKey_State | KeyWordFilterKey
) => b.doc_count - a.doc_count;
const keywordsSortByKey = (
    a: KeyWordFilterKey_State | KeyWordFilterKey,
    b: KeyWordFilterKey_State | KeyWordFilterKey
) => {
    if (typeof a.key === 'string' && typeof b.key === 'string') {
        return a.key.localeCompare(b.key);
    }
    if (a.key > b.key) {
        return 1;
    }
    if (a.key < b.key) {
        return -1;
    }
    return 0;
};

export class KeyWordFilter implements Filter {
    private type: FilterType;

    constructor() {
        this.type = 'keyword';
    }

    public isKeywordFilterConfig(config: Filter_Config): config is KeyWordFilter_Config {
        return config.type === 'keyword';
    }

    public isKeywordFilterState(state: Filter_Config): state is KeyWordFilter_Config {
        return state.type === 'keyword';
    }

    public isKeywordFilterControlsState(
        controlsState: Filter_ControlsState
    ): controlsState is KeyWordFilter_ControlsState {
        return controlsState.type === 'keyword';
    }

    public isKeywordFilterSnapshot(snapshot: Filter_Snapshot): snapshot is KeyWordFilter_Snapshot {
        return (
            typeof snapshot === 'object' &&
            'identifier' in snapshot &&
            typeof snapshot.identifier === 'string' &&
            'values' in snapshot &&
            Array.isArray(snapshot.values)
        );
    }

    public isKeywordFilterAggregation(aggregation: FilterAggregation): aggregation is KeyWordFilterAggregation {
        return (
            Array.isArray(aggregation) &&
            aggregation.every(
                (item) =>
                    'key' in item &&
                    'doc_count' in item &&
                    (typeof item.key === 'string' || typeof item.key === 'number')
            )
        );
    }

    public createStateFromConfig(config: KeyWordFilter_Config): KeyWordFilter_State {
        return {
            ...config,
            values: {
                keywords: this.prepareKeywordsState(config.values.keywords, config.configuration),
            },
        };
    }

    /**
     * If the number filter config has explicit values, we can create a keyword filter state from it
     */
    public createStateFromNumberConfig(config: NumberFilter_Config): KeyWordFilter_State {
        if (config.configuration.subtype !== 'explicitValues' || !config.values.keywords) {
            throw new Error('Cannot create keyword filter state from number filter config without explicit values');
        }
        return {
            ...config,
            type: 'keyword',
            values: {
                keywords: this.prepareKeywordsState(config.values.keywords, config.configuration),
            },
        };
    }

    public createControlsStateFromConfig(config: KeyWordFilter_Config): KeyWordFilter_ControlsState {
        return {
            identifier: config.identifier,
            type: config.type,
            applyFilter: false,
            values: {
                keywords: config.values.keywords.map((keyword) => ({
                    key: keyword.key,
                    isChecked: false,
                })),
            },
        };
    }

    public createControlsStateFromNumberConfig(config: NumberFilter_Config): KeyWordFilter_ControlsState {
        if (config.configuration.subtype !== 'explicitValues' || !config.values.keywords) {
            throw new Error(
                'Cannot create keyword filter controls state from number filter config without explicit values'
            );
        }
        return {
            identifier: config.identifier,
            type: 'keyword',
            applyFilter: false,
            values: {
                keywords: config.values.keywords.map((keyword) => ({
                    ...keyword,
                    isChecked: false,
                })),
            },
        };
    }

    public updateControlsState(
        controlsState: KeyWordFilter_ControlsState,
        state: KeyWordFilter_State,
        payload: UpdateKeyWordsFilterPayload_ControlsState
    ): KeyWordFilter_ControlsState {
        const newKeywords = controlsState.values.keywords.map((keyword) => {
            if (keyword.key !== payload.value.key) {
                return keyword;
            }
            return {
                ...keyword,
                isChecked: payload.value.check,
            };
        });

        return {
            ...controlsState,
            values: {
                ...controlsState.values,
                keywords: newKeywords,
            },
            applyFilter: newKeywords.some((keyword) => keyword.isChecked),
        };
    }

    public applyAggregation(
        state: KeyWordFilter_State,
        controlsState: KeyWordFilter_ControlsState,
        aggregation: KeyWordFilterAggregation
    ): KeyWordFilter_State {
        const keyWordsAfterAggregations = state.values.keywords.map((keyword) => {
            const keywordAggregation = aggregation.find((a) => a.key === keyword.key);
            const keywordControls = controlsState.values.keywords.find((k) => k.key === keyword.key);
            if (!keywordControls) {
                throw new Error(`Keyword ${keyword.key} not found in controls state for filter ${state.identifier}`);
            }
            if (!keywordAggregation) {
                return { ...keyword, doc_count: 0, isDisabled: !keywordControls.isChecked };
            }
            return {
                ...keyword,
                doc_count: keywordAggregation.doc_count,
                isDisabled: keywordAggregation.doc_count === 0 && !keywordControls.isChecked,
            };
        });
        const keyWordsSortedByDisabled = keyWordsAfterAggregations.sort((a, b) => {
            if (a.isDisabled && !b.isDisabled) {
                return 1;
            }
            if (!a.isDisabled && b.isDisabled) {
                return -1;
            }
            return 0;
        });
        const disabledFirstIndex = keyWordsSortedByDisabled.findIndex((k) => k.isDisabled);
        const keyWordsEnabled = keyWordsSortedByDisabled.slice(0, disabledFirstIndex);
        const keyWordsDisabled = keyWordsSortedByDisabled.slice(disabledFirstIndex);
        const newKeywords = [
            ...(this.sortKeywords(keyWordsEnabled, state.configuration) as KeyWordFilterKey_State[]),
            ...(this.sortKeywords(keyWordsDisabled, state.configuration) as KeyWordFilterKey_State[]),
        ];

        return {
            ...state,
            values: {
                keywords: newKeywords,
            },
        };
    }

    public resetControlsState(controlsState: KeyWordFilter_ControlsState): KeyWordFilter_ControlsState {
        return {
            ...controlsState,
            values: {
                ...controlsState.values,
                keywords: controlsState.values.keywords.map((value) => {
                    return {
                        ...value,
                        isChecked: false,
                    };
                }),
            },
            applyFilter: false,
        };
    }

    public cancelFilter(
        controlsState: KeyWordFilter_ControlsState,
        payload: Pick<CancelKeyWordsFilterPayload, 'key' | 'cancelAll'>
    ): KeyWordFilter_ControlsState {
        const newKeywords = controlsState.values.keywords.map((keyword) => {
            if (keyword.key !== payload.key && !payload.cancelAll) {
                return keyword;
            }
            return {
                ...keyword,
                isChecked: false,
            };
        });
        const applyFilter = payload.cancelAll ? false : newKeywords.some((keyword) => keyword.isChecked);
        return {
            ...controlsState,
            values: {
                ...controlsState.values,
                keywords: newKeywords,
            },
            applyFilter,
        };
    }

    public createRequestPayloadFromState(state: KeyWordFilter_ControlsState): O.Option<KeyWordFilter_RequestParams> {
        if (state.applyFilter) {
            return O.some({
                name: state.identifier,
                selectedValues: state.values.keywords
                    .filter((keyword) => keyword.isChecked)
                    .map((keyword) => String(keyword.key)),
            });
        }
        return O.none;
    }

    public createSnapshotFromState(controlsState: KeyWordFilter_ControlsState): O.Option<KeyWordFilter_Snapshot> {
        if (controlsState.applyFilter) {
            return O.some({
                identifier: controlsState.identifier,
                values: controlsState.values.keywords
                    .filter((keyword) => keyword.isChecked)
                    .map((keyword) => keyword.key),
            });
        }
        return O.none;
    }

    public applySnapshotToControlsState(
        controlsState: KeyWordFilter_ControlsState,
        snapshot: KeyWordFilter_Snapshot
    ): KeyWordFilter_ControlsState {
        return {
            ...controlsState,
            values: {
                ...controlsState.values,
                keywords: controlsState.values.keywords.map((keyword) => ({
                    ...keyword,
                    isChecked: snapshot.values.includes(keyword.key),
                })),
            },
            applyFilter: true,
        };
    }

    private prepareKeywordsState(
        keywords: KeyWordFilterKey[],
        configuration: KeyWordFilter_Config['configuration']
    ): Array<KeyWordFilterKey & { isDisabled: boolean }> {
        return this.sortKeywords(keywords, configuration).map((keyword) => ({
            ...keyword,
            isDisabled: keyword.doc_count === 0,
        }));
    }

    private sortKeywords(
        keywords: Array<KeyWordFilterKey_State | KeyWordFilterKey>,
        configuration: KeyWordFilter_Config['configuration']
    ): Array<KeyWordFilterKey_State | KeyWordFilterKey> {
        return keywords.sort(
            configuration.sorting === 'count'
                ? keywordsSortByCount
                : configuration.sorting === 'alphabet'
                  ? keywordsSortByKey
                  : undefined
        );
    }
}
